import React, { useEffect, useState } from 'react';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import {
  getBreakGlassRequests,
  createBreakGlass,
  revokeBreakGlass,
  auditBreakGlass,
  getTeamAccess,
  grantTeamAccess,
  revokeTeamAccess,
  getOrganization,
  updateOrganization,
  getMyTemporaryAccess,
  requestAccessRenewal,
  getRepositories,
  exportAuditToSiem
} from '../services/api';
import './AdminPages.css';

const SecurityCenter = () => {
  const [tab, setTab] = useState('breakglass');
  const [breakGlass, setBreakGlass] = useState([]);
  const [teams, setTeams] = useState([]);
  const [org, setOrg] = useState(null);
  const [repos, setRepos] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bgForm, setBgForm] = useState({ repoId: '', requestedFor: '', permission: 'admin', reason: '', durationHours: 4 });
  const [teamForm, setTeamForm] = useState({ repoId: '', org: '', teamSlug: '', permission: 'pull' });
  const [orgForm, setOrgForm] = useState({
    slackWebhookUrl: '',
    teamsWebhookUrl: '',
    siemWebhookUrl: '',
    ipAllowlist: '',
    requireIpAllowlistForAdmin: false,
    scimBearerToken: ''
  });

  const breakGlassPages = usePagination(breakGlass, 10, breakGlass.length);
  const teamPages = usePagination(teams, 10, teams.length);
  const renewalPages = usePagination(renewals, 10, renewals.length);

  const load = async () => {
    try {
      const [bg, t, o, r, ren] = await Promise.all([
        getBreakGlassRequests(),
        getTeamAccess(),
        getOrganization(),
        getRepositories().catch(() => ({ data: [] })),
        getMyTemporaryAccess().catch(() => ({ data: [] }))
      ]);
      setBreakGlass(bg.data || []);
      setTeams(t.data || []);
      setOrg(o.data);
      setRepos(r.data || []);
      setRenewals((ren.data || []).filter((a) => a.renewalRequested));
      const s = o.data?.settings || {};
      setOrgForm({
        slackWebhookUrl: s.slackWebhookUrl || '',
        teamsWebhookUrl: s.teamsWebhookUrl || '',
        siemWebhookUrl: s.siemWebhookUrl || '',
        ipAllowlist: (s.ipAllowlist || []).join(', '),
        requireIpAllowlistForAdmin: !!s.requireIpAllowlistForAdmin,
        scimBearerToken: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load security data');
    }
  };

  useEffect(() => { load(); }, []);

  const saveOrg = async (e) => {
    e.preventDefault();
    try {
      await updateOrganization({
        settings: {
          slackWebhookUrl: orgForm.slackWebhookUrl,
          teamsWebhookUrl: orgForm.teamsWebhookUrl,
          siemWebhookUrl: orgForm.siemWebhookUrl,
          ipAllowlist: orgForm.ipAllowlist.split(',').map((x) => x.trim()).filter(Boolean),
          requireIpAllowlistForAdmin: orgForm.requireIpAllowlistForAdmin,
          ...(orgForm.scimBearerToken ? { scimBearerToken: orgForm.scimBearerToken } : {})
        }
      });
      setSuccess('Organization settings saved');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    }
  };

  return (
    <div className="admin-page">
      <h1 className="page-title">Security Center</h1>
      <p className="page-subtitle">Break-glass, team grants, Slack/Teams/SIEM, IP allowlist, SCIM token, renewals.</p>
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="filter-buttons">
        {['breakglass', 'teams', 'integrations', 'renewals'].map((t) => (
          <button key={t} className={`filter-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'breakglass' && (
        <>
          <form className="card admin-form" onSubmit={async (e) => {
            e.preventDefault();
            try {
              await createBreakGlass(bgForm);
              setSuccess('Break-glass access granted');
              setBgForm({ repoId: '', requestedFor: '', permission: 'admin', reason: '', durationHours: 4 });
              load();
            } catch (err) {
              setError(err.response?.data?.error || 'Failed');
            }
          }}>
            <h3>Emergency (break-glass) access</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Repository</label>
                <select className="form-select" required value={bgForm.repoId}
                  onChange={(e) => setBgForm({ ...bgForm, repoId: e.target.value })}>
                  <option value="">Select…</option>
                  {repos.map((r) => <option key={r._id} value={r._id}>{r.fullName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">GitHub username</label>
                <input className="form-input" required value={bgForm.requestedFor}
                  onChange={(e) => setBgForm({ ...bgForm, requestedFor: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Hours (1–72)</label>
                <input type="number" className="form-input" min={1} max={72} value={bgForm.durationHours}
                  onChange={(e) => setBgForm({ ...bgForm, durationHours: Number(e.target.value) })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Reason (required for audit)</label>
              <textarea className="form-input" required rows={2} value={bgForm.reason}
                onChange={(e) => setBgForm({ ...bgForm, reason: e.target.value })} />
            </div>
            <button className="btn btn-danger" type="submit">Grant emergency access</button>
          </form>

          <div className="card-list">
            {breakGlassPages.pageItems.map((bg) => (
              <div key={bg._id} className="card">
                <strong>{bg.repository?.fullName}</strong> → {bg.requestedFor} ({bg.permission})
                <div>Status: {bg.status} · Expires: {new Date(bg.expiresAt).toLocaleString()}</div>
                <div>Reason: {bg.reason}</div>
                <div className="repo-actions" style={{ marginTop: 8 }}>
                  {bg.status === 'active' && (
                    <button className="btn btn-sm btn-danger" onClick={async () => { await revokeBreakGlass(bg._id); load(); }}>Revoke</button>
                  )}
                  {!bg.audited && (
                    <button className="btn btn-sm btn-secondary" onClick={async () => {
                      const notes = window.prompt('Post-audit notes');
                      if (notes != null) { await auditBreakGlass(bg._id, notes); load(); }
                    }}>Mark audited</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination
            page={breakGlassPages.page}
            totalPages={breakGlassPages.totalPages}
            total={breakGlassPages.total}
            pageSize={breakGlassPages.pageSize}
            onPageChange={breakGlassPages.setPage}
            label="requests"
          />
        </>
      )}

      {tab === 'teams' && (
        <>
          <form className="card admin-form" onSubmit={async (e) => {
            e.preventDefault();
            try {
              await grantTeamAccess(teamForm);
              setSuccess('Team access granted');
              load();
            } catch (err) {
              setError(err.response?.data?.error || 'Failed');
            }
          }}>
            <h3>Grant GitHub team access</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Repository</label>
                <select className="form-select" required value={teamForm.repoId}
                  onChange={(e) => setTeamForm({ ...teamForm, repoId: e.target.value })}>
                  <option value="">Select…</option>
                  {repos.map((r) => <option key={r._id} value={r._id}>{r.fullName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Org</label>
                <input className="form-input" required value={teamForm.org}
                  onChange={(e) => setTeamForm({ ...teamForm, org: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Team slug</label>
                <input className="form-input" required value={teamForm.teamSlug}
                  onChange={(e) => setTeamForm({ ...teamForm, teamSlug: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Permission</label>
                <select className="form-select" value={teamForm.permission}
                  onChange={(e) => setTeamForm({ ...teamForm, permission: e.target.value })}>
                  <option value="pull">pull</option>
                  <option value="push">push</option>
                  <option value="admin">admin</option>
                  <option value="maintain">maintain</option>
                  <option value="triage">triage</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" type="submit">Grant team access</button>
          </form>
          <div className="card-list">
            {teamPages.pageItems.map((t) => (
              <div key={t._id} className="card">
                {t.org}/{t.teamSlug} → {t.repository?.fullName} ({t.permission})
                <button className="btn btn-sm btn-danger" style={{ marginLeft: 8 }}
                  onClick={async () => { await revokeTeamAccess(t._id); load(); }}>Revoke</button>
              </div>
            ))}
          </div>
          <Pagination
            page={teamPages.page}
            totalPages={teamPages.totalPages}
            total={teamPages.total}
            pageSize={teamPages.pageSize}
            onPageChange={teamPages.setPage}
            label="teams"
          />
        </>
      )}

      {tab === 'integrations' && (
        <form className="card admin-form" onSubmit={saveOrg}>
          <h3>Notifications & compliance</h3>
          <p>Webhook status: Slack {org?.webhookStatus?.slack ? '✓' : '—'} · Teams {org?.webhookStatus?.teams ? '✓' : '—'} · SIEM {org?.webhookStatus?.siem ? '✓' : '—'}</p>
          <div className="form-group">
            <label className="form-label">Slack webhook URL</label>
            <input className="form-input" value={orgForm.slackWebhookUrl}
              onChange={(e) => setOrgForm({ ...orgForm, slackWebhookUrl: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Teams webhook URL</label>
            <input className="form-input" value={orgForm.teamsWebhookUrl}
              onChange={(e) => setOrgForm({ ...orgForm, teamsWebhookUrl: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">SIEM webhook URL</label>
            <input className="form-input" value={orgForm.siemWebhookUrl}
              onChange={(e) => setOrgForm({ ...orgForm, siemWebhookUrl: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Admin IP allowlist (comma-separated CIDRs/IPs)</label>
            <input className="form-input" value={orgForm.ipAllowlist}
              onChange={(e) => setOrgForm({ ...orgForm, ipAllowlist: e.target.value })} />
          </div>
          <label className="checkbox-row">
            <input type="checkbox" checked={orgForm.requireIpAllowlistForAdmin}
              onChange={(e) => setOrgForm({ ...orgForm, requireIpAllowlistForAdmin: e.target.checked })} />
            Enforce IP allowlist for admin actions
          </label>
          <div className="form-group">
            <label className="form-label">SCIM bearer token (set/rotate)</label>
            <input className="form-input" type="password" placeholder="Leave blank to keep current"
              value={orgForm.scimBearerToken}
              onChange={(e) => setOrgForm({ ...orgForm, scimBearerToken: e.target.value })} />
            <small>IdP SCIM base URL: <code>/api/scim/v2</code></small>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">Save</button>
            <button type="button" className="btn btn-secondary" onClick={async () => {
              const res = await exportAuditToSiem(24);
              setSuccess(`Exported ${res.data.sent}/${res.data.total} events to SIEM`);
            }}>Export last 24h audit to SIEM</button>
          </div>
        </form>
      )}

      {tab === 'renewals' && (
        <div className="card-list">
          {renewals.length === 0 && <p>No pending renewal requests.</p>}
          {renewalPages.pageItems.map((a) => (
            <div key={a._id} className="card">
              <strong>{a.repository?.fullName}</strong> — {a.githubUsername}
              <div>Expires: {new Date(a.expiresAt).toLocaleString()}</div>
              <div>Reason: {a.renewalReason || '—'}</div>
              <button className="btn btn-sm btn-primary" onClick={async () => {
                await requestAccessRenewal(a._id, '', 14);
                setSuccess('Extended 14 days');
                load();
              }}>Extend 14 days</button>
            </div>
          ))}
          <Pagination
            page={renewalPages.page}
            totalPages={renewalPages.totalPages}
            total={renewalPages.total}
            pageSize={renewalPages.pageSize}
            onPageChange={renewalPages.setPage}
            label="renewals"
          />
        </div>
      )}
    </div>
  );
};

export default SecurityCenter;
